import {Component, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {EmployeeService} from '../../services/http/employee/employee.service';
import {Employee} from '../../classes/models/employee/Employee';
import {DataService} from '../../services/store/data.service';
import moment from 'moment';
import {StoreEmployeeAvailability} from '../../classes/models/employee/StoreEmployeeAvailability';
import {map, takeUntil} from 'rxjs/operators';
import {EmployeesSortService} from '../../services/employee/employees-sort.service';
import {EmployeeAvailabilityService} from '../../services/http/employee/employee-availability.service';
import {Subject} from 'rxjs';
import {SHARED_IMPORTS} from "../../shared/standalone-imports";
import {WeekSelectComponent} from "../../shared/components/week-select/week-select.component";
import {EmployeesListComponent} from "./employees-list/employees-list.component";
import {EmployeesAvailabilitiesComponent} from "./employees-availabilities/employees-availabilities.component";
import {EmployeesCapabilitiesComponent} from "./employees-capabilities/employees-capabilities.component";

/**
 * This component displays the main page of all employees for a selected store.
 */
@Component({
  selector: 'app-employees',
  templateUrl: './employees.component.html',
  styleUrls: ['./employees.component.less'],
  imports: [SHARED_IMPORTS, WeekSelectComponent, EmployeesListComponent, EmployeesAvailabilitiesComponent, EmployeesCapabilitiesComponent]
})
export class EmployeesComponent implements OnInit {
  dataService = inject(DataService);
  /** An identifier of a store. */
  storeId = signal<string>(undefined);
  /** A list of all employees for a store. */
  employees = signal<Employee[]>([]);
  /** A list of all availabilities for all employees. */
  availabilities = signal<StoreEmployeeAvailability[]>([]);
  /** A Monday, from which a week of availabilities or capabilities are displayed. */
  selectedMonday = signal<string>(undefined);
  /** The last Monday, until which availabilities and capabilities are displayed. */
  lastMonday: string;
  /** The pages of employees, who can be switched in this component. */
  pages = {
    list: 'List',
    availabilities: 'Availabilities',
    capabilities: 'Capabilities'
  };
  /** Sets the default page to employees list. */
  page = signal(this.pages.list);
  private route = inject(ActivatedRoute);
  private employeeService = inject(EmployeeService);
  private employeeAvailabilityService = inject(EmployeeAvailabilityService);
  private employeesSortService = inject(EmployeesSortService);
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroy$.next();
      this.destroy$.complete();
    });
  }

  /**
   * Retrieves all employee data from backend.
   */
  ngOnInit(): void {
    this.route.params
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.storeId.set(this.route.snapshot.paramMap.get('storeId'));
                const currentWeekMonday = moment().startOf('week').isoWeekday(1).format('YYYY-MM-DD');
                this.getEmployees(this.storeId());
                this.getAvailabilities(this.storeId());
                this.selectWeek(currentWeekMonday);
                this.lastMonday = moment(this.dataService.rangeTo, 'YYYY-MM-DD')
                        .subtract(6, 'day')
                        .format('YYYY-MM-DD');
              }
            });
  }

  /**
   * Selects a new week, for which employee's availabilities or capabilities will be showed.
   * @param weekStart a Monday, from which the new selected week starts.
   */
  selectWeek(weekStart: string): void {
    this.selectedMonday.set(weekStart);
  }

  /**
   * Returns a list of dates for week, starting from the Monday of the selected week.
   */
  getDatesForSelectedWeek(): string[] {
    const dates = [];
    const startDate = moment(this.selectedMonday());
    const endDate = moment(this.selectedMonday()).add(6, 'days');
    for (let date = startDate; date <= endDate; date = date.add(1, 'days')) {
      dates.push(date.format('YYYY-MM-DD'));
    }
    return dates;
  }

  /**
   * Checks if the current page is an employee's list.
   */
  isListPageActive(): boolean {
    return this.page() === this.pages.list;
  }

  /**
   * Switches to the other employee's page.
   * @param page a new page to switch.
   */
  setPage(page: string): void {
    this.page.set(page);
  }

  private getEmployees(storeId: string): void {
    this.employeeService.get(storeId)
            .pipe(
                    map(employees => this.employeesSortService.sortByRole(employees)),
                    takeUntil(this.destroy$)
            )
            .subscribe({
              next: employees => this.employees.set(employees)
            });
  }

  private getAvailabilities(storeId: string): void {
    this.employeeAvailabilityService.getAvailabilities(storeId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: availabilities => this.availabilities.set(availabilities)
            });
  }
}